<div class="p-6">

  <!-- Recently Visited Section -->
  @if (recentVenues.length > 0) {
    <div class="mb-8">
      <h2 class="text-lg font-semibold text-gray-900 mb-3">Recently Visited</h2>
      <div class="flex gap-4 overflow-x-auto pb-2">
        @for (recent of recentVenues; track recent.venueId) {
          <div class="relative flex-shrink-0 w-48 bg-white border border-gray-200 rounded-lg shadow-sm
                      hover:shadow-md transition-shadow duration-200 overflow-hidden">
            <button
              (click)="removeRecentVenue(recent.venueId)"
              [title]="'Remove ' + recent.name + ' from recently visited'"
              class="absolute top-1.5 right-1.5 z-10 p-1 rounded-full bg-white/80 text-gray-500
                     hover:bg-white hover:text-gray-800 shadow-sm
                     focus:outline-none focus:ring-2 focus:ring-gray-300"
            >
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
            <a [routerLink]="['/venues', recent.venueId]" class="block">
              <div class="w-full h-24 overflow-hidden bg-gray-100">
                <img [src]="recent.imagePath || placeholderImage" [alt]="recent.name"
                  width="192" height="96" fetchpriority="high" decoding="async"
                  (error)="onImageError($event)"
                  class="object-cover object-center w-full h-full" />
              </div>
              <div class="p-2">
                <p class="text-sm font-medium text-gray-900 truncate">{{ recent.name }}</p>
                @if (recent.enabled) {
                  <span class="inline-block mt-1 px-1.5 py-0.5 text-xs font-semibold text-green-700 bg-green-100 rounded">Enabled</span>
                } @else {
                  <span class="inline-block mt-1 px-1.5 py-0.5 text-xs font-semibold text-red-700 bg-red-100 rounded">Disabled</span>
                }
              </div>
            </a>
            <div class="flex border-t border-gray-200">
              <a [routerLink]="['/venues', recent.venueId]"
                 class="flex-1 py-1.5 text-xs font-medium text-center text-white bg-black hover:bg-gray-800
                        border-r border-gray-200">
                Admin
              </a>
              <a [href]="getPortalUrl(recent.venueId)" target="_blank" rel="noopener noreferrer"
                (click)="onPortalClick(recent.venueId, recent.name, recent.imagePath, recent.enabled)"
                class="flex-1 py-1.5 text-xs font-medium text-center text-black hover:bg-gray-100 border-r border-gray-200">
                Portal
              </a>
              <a [href]="getOrdersUrl(recent.venueId)" target="_blank" rel="noopener noreferrer"
                class="flex-1 py-1.5 text-xs font-medium text-center text-black hover:bg-gray-100">
                Orders
              </a>
            </div>
          </div>
        }
      </div>
    </div>
  }

  <!-- Search + Activity Toggle -->
  <div class="flex flex-wrap items-center gap-4 mb-4">
    <div class="relative">
      <input
        type="text"
        [(ngModel)]="searchTerm"
        (ngModelChange)="onSearchChange()"
        [placeholder]="useActivityData ? 'Search venues, emails, contacts...' : 'Search venues...'"
        autocomplete="off"
        autocorrect="off"
        autocapitalize="off"
        spellcheck="false"
        class="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        />
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"
        class="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
        <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
      </svg>
    </div>

    <label class="inline-flex items-center gap-2 cursor-pointer select-none">
      <span class="text-sm text-gray-600 dark:text-gray-400">Activity data</span>
      <div class="relative">
        <input type="checkbox" class="sr-only peer"
          [(ngModel)]="useActivityData"
          (ngModelChange)="onActivityToggleChange()" />
        <div class="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:bg-black
                    peer-focus:ring-2 peer-focus:ring-gray-300 transition-colors"></div>
        <div class="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow
                    transition-transform peer-checked:translate-x-full"></div>
      </div>
    </label>

    @if (useActivityData) {
      @if (activityLoading) {
        <span class="inline-flex items-center gap-1.5 text-sm text-gray-500">
          <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Loading activity data...
        </span>
      } @else if (activityError) {
        <span class="text-sm text-red-600">{{ activityError }}</span>
      } @else if (activityDataset) {
        <span class="text-xs text-gray-500 dark:text-gray-400">
          {{ activityFromCache ? 'Cached' : 'Fresh' }} · generated {{ activityDataset.generatedAt | date:'MMM d, h:mm a' }}
        </span>
      }
    }
  </div>

  @if (filteredVenues$ | async; as venues) {
    @if (venues.length === 0 && searchTerm) {
      <div class="text-center py-12">
        <p class="text-gray-500 dark:text-gray-400 mb-4">No venues found</p>
        <button
          (click)="clearSearch()"
          class="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
          >
        Remove filter
      </button>
    </div>
  } @else {
    <!-- Venues Table -->
    <div class="overflow-x-auto bg-white border border-gray-200 rounded-lg shadow-sm">
      <table class="w-full text-sm text-left text-gray-500">
        <thead class="text-xs text-gray-700 uppercase bg-gray-50">
          <tr>
            <th scope="col" class="px-6 py-3">Venue</th>
            @if (useActivityData && activityDataset) {
              <th scope="col" class="px-6 py-3" title="Order count and per-day activity for the last 7 days">Orders — last 7 days</th>
            }
            <th scope="col" class="px-6 py-3">Status</th>
            <th scope="col" class="px-6 py-3 text-right">
              <label class="inline-flex items-center gap-2 cursor-pointer font-normal normal-case">
                <span class="text-xs text-gray-600">Enabled only</span>
                <div class="relative">
                  <input type="checkbox" class="sr-only peer"
                    [(ngModel)]="showEnabledOnly"
                    (ngModelChange)="onEnabledFilterChange()" />
                  <div class="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:bg-black
                              peer-focus:ring-2 peer-focus:ring-gray-300 transition-colors"></div>
                  <div class="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow
                              transition-transform peer-checked:translate-x-full"></div>
                </div>
              </label>
            </th>
          </tr>
        </thead>
        <tbody>
          @for (item of venues; track trackById($index, item)) {
            <tr
              class="bg-white border-b hover:bg-gray-50 transition-colors">
              <td class="px-6 py-4">
                <div class="flex items-center gap-3">
                  <img [src]="item.imagePath || placeholderImage" [alt]="item.name"
                    width="40" height="40" loading="lazy" decoding="async"
                    (error)="onImageError($event)"
                    class="w-10 h-10 rounded-full object-cover flex-shrink-0 bg-gray-100" />
                  <div class="min-w-0">
                    <span class="font-medium text-gray-900 block">{{ item.name }}</span>
                    @if (item.activity && item.activity.email) {
                      <span class="text-xs text-gray-500 truncate block">{{ item.activity.email }}</span>
                    }
                  </div>
                </div>
              </td>
              @if (useActivityData && activityDataset) {
                <td class="px-6 py-4">
                  @if (item.activity) {
                    <div class="flex items-end gap-3">
                      <span class="font-semibold text-gray-900 tabular-nums w-10 text-right">{{ item.activity.orderCounts.total }}</span>
                      <div class="flex items-end gap-0.5 h-8">
                        @for (day of item.activity.orderCounts.byDay; track day.date) {
                          <div
                            class="w-2 rounded-sm min-h-[2px]"
                            [class]="day.count > 0 ? 'bg-black' : 'bg-gray-200'"
                            [style.height.%]="barHeightPct(item.activity, day.count)"
                            [title]="dayLabel(day.date) + ': ' + day.count + ' orders'"
                          ></div>
                        }
                      </div>
                    </div>
                  } @else {
                    <span class="text-gray-300">—</span>
                  }
                </td>
              }
              <td class="px-6 py-4">
                @if (item.enabled) {
                  <span class="inline-block px-2 py-1 text-xs font-semibold text-green-700 bg-green-100 rounded">Enabled</span>
                } @else {
                  <span class="inline-block px-2 py-1 text-xs font-semibold text-red-700 bg-red-100 rounded">Disabled</span>
                }
              </td>
              <td class="px-6 py-4 text-right">
                <div class="flex items-center justify-end gap-2">
                  <a [routerLink]="['/venues', item.id]"
                   class="px-3 py-1.5 text-sm font-medium text-white bg-black rounded hover:bg-gray-800
                          focus:ring-2 focus:outline-none focus:ring-gray-300">
                    Admin
                  </a>
                  <a [href]="getPortalUrl(item.id)" target="_blank" rel="noopener noreferrer"
                    (click)="onPortalClick(item.id, item.name, item.imagePath, item.enabled)"
                   class="px-3 py-1.5 text-sm font-medium text-black bg-white border border-black rounded hover:bg-gray-100
                          focus:ring-2 focus:outline-none focus:ring-gray-300">
                    Portal
                  </a>
                  <a [href]="getOrdersUrl(item.id)" target="_blank" rel="noopener noreferrer"
                   class="px-3 py-1.5 text-sm font-medium text-black bg-white border border-black rounded hover:bg-gray-100
                          focus:ring-2 focus:outline-none focus:ring-gray-300">
                    Orders
                  </a>
                </div>
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
    }
  } @else {
    <!-- Loading State -->
    <div class="flex items-center justify-center py-24">
      <div class="text-center">
        <svg class="animate-spin h-12 w-12 text-black dark:text-white mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p class="text-gray-600 dark:text-gray-400">Loading venues...</p>
      </div>
    </div>
  }
</div>
